import { useEffect, useRef } from 'react'
import { useEntityState } from '@/store/hooks'

/**
 * Auto-update: HA holds the deployed build id in input_text.dashboard_build.
 * When it differs from the running bundle's __BUILD_ID__, reload — but only
 * while the page is visible, with a 0–60s random jitter so a fleet of wall
 * tablets doesn't reload in lockstep. Never reloads on the first (own) value.
 */
export function UpdateWatcher() {
  const deployed = useEntityState('input_text.dashboard_build')
  const scheduled = useRef(false)

  useEffect(() => {
    if (!deployed || deployed === 'unknown' || deployed === 'unavailable') return
    if (deployed === __BUILD_ID__) return
    if (scheduled.current) return

    // Guard against a reload loop: if we already reloaded targeting this build id
    // but the running bundle still doesn't match it (bad/partial deploy, or an id
    // HA holds that was never actually served), don't reload again — we'd just spin.
    let lastTarget: string | null = null
    try {
      lastTarget = sessionStorage.getItem('dashboard_build_reload')
    } catch {
      /* storage disabled — fall through and allow a single reload */
    }
    if (lastTarget === deployed) return
    scheduled.current = true

    const jitter = Math.random() * 60_000
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem('dashboard_build_reload', deployed)
      } catch {
        /* storage disabled — the loop guard simply won't persist */
      }
      const go = () => location.reload()
      if (document.visibilityState === 'visible') go()
      else document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && go(), { once: true })
    }, jitter)
    return () => clearTimeout(timer)
  }, [deployed])

  return null
}
